/**
 * EnvironmentBadge Component
 * Displays a badge with environment-specific styling for visual environment indicators.
 * @packageDocumentation
 */

import { AlertTriangle } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { Environment } from '@/types/retailer';

import { Badge } from './badge';

/** Environment badge color styles using semantic design tokens. */
export const ENV_BADGE_COLORS: Record<Environment, string> = {
  development: 'bg-success/10 text-success dark:bg-success/20',
  staging: 'bg-warning/10 text-warning-foreground dark:bg-warning/20',
  production: 'bg-destructive/10 text-destructive dark:bg-destructive/20',
};

/**
 * Props for the EnvironmentBadge component.
 */
export interface EnvironmentBadgeProps {
  /** Additional CSS classes. */
  className?: string;
  /** The environment display name. */
  displayName: string;
  /** The retailer name to display. */
  retailerName: string;
  /** The environment type. */
  environment: Environment;
}

/**
 * Badge component that displays environment-specific styling.
 * Shows a warning icon for production environments.
 *
 * @param { EnvironmentBadgeProps } props Component props.
 * @returns { JSX.Element } The environment badge element.
 */
export const EnvironmentBadge = ({ className, environment, displayName, retailerName }: EnvironmentBadgeProps) => (
  <Badge variant="outline" className={cn('shrink-0 text-xs', ENV_BADGE_COLORS[environment], className)}>
    {environment === 'production' && <AlertTriangle className="mr-1 size-3" />}
    {retailerName} • {displayName}
  </Badge>
);
